import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";

import { useBlogPostDetail } from "../hooks/useBlogPostDetail";
import { formatDisplayDate } from "../utils/formatDisplayDate";

const FALLBACK_IMAGE = "/images/initiatives/Picture1.png";

const NewsletterDetail: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();
  const { post: apiPost, loading, error } = useBlogPostDetail(slug);

  useEffect(() => {
    document.body.classList.add("page-newsletter-details");
    return () => {
      document.body.classList.remove("page-newsletter-details");
    };
  }, []);

  const detailPost = useMemo(() => {
    if (!apiPost) return null;
    const tags = Array.isArray(apiPost.eng_tags)
      ? apiPost.eng_tags
          .map((tag) => tag?.tag_name_eng)
          .filter((tagName): tagName is string => Boolean(tagName))
      : [];

    return {
      title: apiPost.eng_title || "Newsletter",
      heroImage: apiPost.eng_thumbnail || FALLBACK_IMAGE,
      excerpt: apiPost.eng_caption || "",
      date: formatDisplayDate(
        (apiPost as { created_at?: string }).created_at ||
          apiPost.created_date ||
          apiPost.published_at ||
          ""
      ),
      tags,
      content:
        apiPost.eng_content ||
        apiPost.eng_description ||
        apiPost.eng_caption ||
        "",
    };
  }, [apiPost]);

  const pageBgStyle = useMemo(
    () => ({
      backgroundImage: detailPost
        ? `url('${detailPost.heroImage}')`
        : undefined,
      backgroundSize: "cover",
      backgroundPosition: "center",
      paddingTop: "140px",
      paddingBottom: "80px",
      color: "white",
      textAlign: "center" as const,
      position: "relative" as const,
      overflow: "hidden" as const,
    }),
    [detailPost]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading newsletter...</span>
          </div>
        </div>
      );
    }

    if (error && !detailPost) {
      return (
        <p>Failed to load newsletter. Please try again later.</p>
      );
    }

    if (!detailPost) {
      return <p>We could not find the requested newsletter.</p>;
    }

    return (
      <>
        <div className="blog-details__img mb-4">
          <img src={detailPost.heroImage} alt={detailPost.title} />
        </div>

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
          <div className="d-flex flex-column gap-2">
            {detailPost.tags.length > 0 && (
              <div className="blog-details__tags d-flex gap-2 align-items-center flex-wrap">
                {detailPost.tags.map((tag, index) => (
                  <Link
                    key={index}
                    className="tag"
                    to={`/tag/${encodeURIComponent(tag)}`}
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}
            {detailPost.date && (
              <span className="blog-card__date">
                <i className="fas fa-calendar"></i> {detailPost.date}
              </span>
            )}
          </div>
        </div>

        {detailPost.content ? (
          <div
            className="blog-details__text"
            dangerouslySetInnerHTML={{ __html: detailPost.content }}
          />
        ) : (
          <p>No content available for this newsletter.</p>
        )}
      </>
    );
  };

  return (
    <main id="main-content" role="main">
      <section className="page-title" style={pageBgStyle}>
        <div
          className="page-title__overlay"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "linear-gradient(135deg, rgba(0, 51, 102, 0.8) 0%, rgba(0, 102, 204, 0.8) 100%)",
            zIndex: 1,
          }}
        />
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="page-title__inner">
            <div className="page-title__title-box">
              <h1 className="page-title__title" style={{ color: "#ffffff" }}>
                {detailPost ? detailPost.title : "Newsletter"}
              </h1>
            </div>
            <p className="page-title__text">
              {detailPost ? detailPost.excerpt : ""}
            </p>
          </div>
        </div>
      </section>

      <section className="blog-details pt-5 pb-5 bg-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="blog-details__content">
                {renderContent()}
                <div className="d-flex mt-5">
                  <Link to="/newsletter" className="btn-main btn-line">
                    <i className="fas fa-arrow-left"></i> Back to Newsletter
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default NewsletterDetail;
